import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardBody, Button } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { ChevronDown, ChevronRight, Plus, Pencil, Trash2, Boxes } from 'lucide-react';
import { formatDate, formatMoney } from '@/lib/format';
import {
  useServiceConfigoptions,
  useConfigOptionPriceHistory,
  type ServiceConfigOption,
  type ServiceConfigOptionPrice,
} from '@/api/services';
import { useDeleteConfigOptionPrice } from '@/api/services-mutations';
import { TabLoading } from '@/pages/customers/detail/tab-states';
import { ConfigPriceDialog } from './service-config-price-dialog';

const muted = { color: 'var(--vn-fg-muted)', fontStyle: 'italic' } as const;
const nf = new Intl.NumberFormat('en-US');
const rightHeader = (label: string) => () => <span style={{ display: 'block', textAlign: 'right' }}>{label}</span>;
const rightNum = (v: number) => <span style={{ display: 'block', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{nf.format(v)}</span>;

/**
 * BundleConfigPrices — per-config-option committed-price history. Each config
 * option (bandwidth commitment / VM package / …) expands to its price rows with
 * add / edit / delete, mirroring legacy Prices › BundleConfigPrices.
 */
export function ServiceConfigPricesSection({ serviceId, currency = 'VND' }: { serviceId: number; currency?: string }) {
  const { data, isLoading } = useServiceConfigoptions(serviceId);
  const options = data?.results ?? [];
  if (isLoading) return null;
  if (!options.length) return null;

  return (
    <Card flat>
      <CardHeader>
        <CardTitle>Config option prices</CardTitle>
      </CardHeader>
      <CardBody>
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {options.map((o) => (
            <ConfigOptionRow key={o.service_configoption_id} serviceId={serviceId} option={o} currency={currency} />
          ))}
        </div>
      </CardBody>
    </Card>
  );
}

function ConfigOptionRow({ serviceId, option, currency = 'VND' }: { serviceId: number; option: ServiceConfigOption; currency?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <Card flat>
      <Button
        type="button"
        variant="ghost"
        onClick={() => setOpen((v) => !v)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', padding: 'var(--vn-space-2) var(--vn-space-3)', background: 'var(--vn-bg-subtle)', textAlign: 'left', borderRadius: 0 }}
      >
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{option.name}</span>
        {option.unit && option.unit !== 'qty' ? <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)', textTransform: 'uppercase' }}>({option.unit})</span> : null}
      </Button>
      {open ? <ConfigPriceHistory serviceId={serviceId} configId={option.service_configoption_id} configName={option.name} currency={currency} /> : null}
    </Card>
  );
}

function ConfigPriceHistory({ serviceId, configId, configName, currency = 'VND' }: { serviceId: number; configId: number; configName: string; currency?: string }) {
  const toast = useToast();
  const { data, isLoading } = useConfigOptionPriceHistory(serviceId, configId);
  const del = useDeleteConfigOptionPrice(serviceId, configId);
  const [dialog, setDialog] = useState<{ open: boolean; row: ServiceConfigOptionPrice | null }>({ open: false, row: null });
  const [toDelete, setToDelete] = useState<ServiceConfigOptionPrice | null>(null);

  const rows = data?.results ?? [];

  const columns: ColumnDef<ServiceConfigOptionPrice, unknown>[] = [
    { id: 'commitment_value', header: rightHeader('Commitment'), cell: ({ row }) => rightNum(row.original.commitment_value) },
    { id: 'unit_price', header: rightHeader('Unit price'), cell: ({ row }) => <span style={{ display: 'block', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatMoney(row.original.commitment_price_per_unit, currency)}</span> },
    { id: 'effective_from', header: rightHeader('From'), cell: ({ row }) => <span style={{ display: 'block', textAlign: 'right' }}>{formatDate(row.original.effective_from)}</span> },
    { id: 'effective_to', header: rightHeader('To'), cell: ({ row }) => <span style={{ display: 'block', textAlign: 'right' }}>{row.original.effective_to ? formatDate(row.original.effective_to) : <span style={muted}>ongoing</span>}</span> },
    { id: 'source', header: 'Source', cell: ({ row }) => <span style={{ textTransform: 'capitalize' }}>{row.original.source || '—'}</span> },
    { id: 'description', header: 'Description', cell: ({ row }) => row.original.description || <span style={muted}>—</span> },
    {
      id: 'actions',
      header: rightHeader('Actions'),
      cell: ({ row }) => (
        <span style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--vn-space-1)' }}>
          <Button size="sm" variant="ghost" aria-label="Edit" onClick={() => setDialog({ open: true, row: row.original })}><Pencil size={16} /></Button>
          <Button size="sm" variant="ghost" aria-label="Delete" onClick={() => setToDelete(row.original)}><Trash2 size={16} /></Button>
        </span>
      ),
    },
  ];

  const confirmDelete = () => {
    if (!toDelete) return;
    del.mutate(toDelete.id, {
      onSuccess: () => { toast.success('Price deleted'); setToDelete(null); },
      onError: (e) => toast.error('Failed to delete price', { description: e instanceof Error ? e.message : undefined }),
    });
  };

  return (
    <>
      <CardHeader
        actions={
          <Button size="sm" variant="secondary" onClick={() => setDialog({ open: true, row: null })}><Plus size={16} /> Add price</Button>
        }
      />
      <CardBody>
        {isLoading ? (
          <TabLoading rows={2} />
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            getRowId={(r) => String(r.id)}
            emptyState={<EmptyState icon={<Boxes size={20} />} title="No price history" description="No committed prices recorded for this config option." />}
          />
        )}
        <ConfigPriceDialog
          serviceId={serviceId}
          configId={configId}
          configName={configName}
          open={dialog.open}
          price={dialog.row}
          onOpenChange={(o) => setDialog((s) => ({ open: o, row: o ? s.row : null }))}
        />
        <ConfirmDialog
          open={!!toDelete}
          onOpenChange={(o) => (o ? undefined : setToDelete(null))}
          title="Delete price"
          description="This removes the committed-price row. This action cannot be undone."
          confirmLabel="Delete"
          destructive
          loading={del.isPending}
          onConfirm={confirmDelete}
        />
      </CardBody>
    </>
  );
}
